import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { breadcrumbsIntegration, init as sentryInit } from "@sentry/react";
import { datadogRum } from "@datadog/browser-rum";
import { AuthProvider, useAuth } from "./login/provider";
import { GraphqlProvider } from "./providers/graphql";
import { ViewerProvider } from "./providers/viewer";
import { MondooThemeProvider } from "./providers/mondoo-theme";
import { ColorModeProvider } from "./providers/color-mode";
import { GlobalWords } from "./lib/global.words";
import { version } from "../package.json";

import AuthenticatedAppRoutes from "~/routes/authenticated-app/authenticatedApp-routes";
import UnauthenticatedApp, {
  isUnauthRoute,
} from "./unauthenticatedApp/unauthenticatedApp";
import { Config } from "./constants/configuration_provider";
import { AppNavDrawerProvider } from "./providers/app-nav-drawer";
import { ReportingApp } from "./reportApp/reportingApp";
import { RegionsProvider } from "./providers/RegionsProvider";
import { ScopeProvider } from "./providers/ScopeProvider";
import { DialogsProvider } from "@toolpad/core";
import { TriageToolbarProvider } from "~/providers/triage-toolbar";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider";
import { FeatureFlagsModal } from "./lib/FeatureFlags/FeatureFlagsModal";

const isLocalhost = window.location.hostname === "localhost";
const sentryDsn = Config.VITE_SENTRY_DSN || "";
const sentryEnv = Config.VITE_SENTRY_ENVIRONMENT || "";
const sentryIsEnabled = Config.VITE_SENTRY_ENABLE === "true";
const sentryHasConfig = sentryDsn.length > 0 && sentryEnv.length > 0;
const sentryCanInit = sentryIsEnabled && sentryHasConfig && !isLocalhost;

// initialize sentry
if (sentryCanInit) {
  try {
    sentryInit({
      dsn: sentryDsn,
      environment: sentryEnv,
      integrations: [
        breadcrumbsIntegration({
          dom: { serializeAttribute: ["data-testid", "id", "class"] },
        }),
      ],
    });
    console.log("Sentry initialized");
  } catch (error) {
    console.error("Sentry failed to initialize:", error);
  }
}

const datadogIsEnabled = Config.VITE_DATADOG_ENABLE === "true";
const datadogAppId = Config.VITE_DATADOG_APP_ID || "";
const datadogClientToken = Config.VITE_DATADOG_CLIENT_TOKEN || "";
const datadogEnv = Config.VITE_DATADOG_ENVIRONMENT || "";
const datadogService = Config.VITE_DATADOG_SERVICE || "mondoo-console";
const datadogSessionSampleRate =
  Config.VITE_DATADOG_SESSION_SAMPLE_RATE || "100";
const datadogSessionReplaySampleRate =
  Config.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE || "100";
const datadogWorkerUrl = new URL(
  "../node_modules/@datadog/browser-worker/bundle/worker.js",
  import.meta.url,
).href;
const datadogHasConfig = [datadogAppId, datadogClientToken].every(
  (v) => v.length > 0,
);
const datadogCanInit = datadogIsEnabled && datadogHasConfig && !isLocalhost;

// initialize datadog
if (datadogCanInit) {
  try {
    datadogRum.init({
      applicationId: datadogAppId,
      clientToken: datadogClientToken,
      site: "datadoghq.com",
      proxy: "https://dd-metrics.mondoo.com",
      env: datadogEnv,
      service: datadogService,
      version,
      sessionSampleRate: parseInt(datadogSessionSampleRate),
      sessionReplaySampleRate: parseInt(datadogSessionReplaySampleRate),
      defaultPrivacyLevel: "mask-user-input",
      workerUrl: datadogWorkerUrl,
      excludedActivityUrls: [/\/generated-report\/.*/],
    });
    console.log("Datadog initialized");
  } catch (error) {
    console.error("Datadog failed to initialize:", error);
  }
}

// initialize GlobalWords
GlobalWords.enable();

// Load app
const App = () => {
  const { state } = useAuth();

  const loggedIn = state.user?.emailVerified;
  const currentUrl = new URL(window.location.href);
  const reportView = currentUrl.pathname.includes("/generated-report");

  if (reportView) {
    return (
      <RegionsProvider>
        <ReportingApp />
      </RegionsProvider>
    );
  }

  // When signing up for the first time, we are checking to see if the user
  // has selected the box to opt into email updates
  let emailOptIn = currentUrl.searchParams.get("emailOptIn");
  if (!emailOptIn) {
    emailOptIn = localStorage.getItem("emailOptIn");
  }
  if (!loggedIn && emailOptIn) {
    localStorage.setItem("mondoo_emailOptIn", emailOptIn);
  }

  let redirectTo = currentUrl.searchParams.get("redirectTo");

  if (!redirectTo) {
    redirectTo = localStorage.getItem("redirectTo");
  }

  // If not logged in and not navigating to an UnauthenticatedApp route
  if (!loggedIn && !isUnauthRoute(currentUrl)) {
    // Redirect to login and remember the original destination url
    const href = currentUrl.pathname + currentUrl.search;
    localStorage.setItem("redirectTo", href);
    window.location.href = `/login?redirectTo=${href}`;
  }

  // If not logged in and navigating to an UnauthenticatedApp route
  // we want to clear out the redirectTo param otherwise the user will
  // end up on the dashboard after logging in
  if (!loggedIn && isUnauthRoute(currentUrl)) {
    localStorage.setItem("redirectTo", "/");
  }

  // If logged in and current url contains a redirectTo search param
  if (loggedIn && redirectTo) {
    try {
      // check that URL is relative, if not relative - function will throw error
      // into catch block
      isRelativeUrl(redirectTo);
      // Redirect to the destination url specified by redirectTo
      const { pathname, search } = new URL(window.location.origin + redirectTo);
      window.location.href = pathname + search;
    } catch {
      // redirectTo param was malformed so just redirect to root path
      window.location.href = "/";
    } finally {
      localStorage.removeItem("redirectTo");
    }
  }

  return loggedIn ? (
    <RegionsProvider>
      <GraphqlProvider>
        <BrowserRouter>
          <ViewerProvider>
            <ColorModeProvider>
              <ScopeProvider>
                <AppNavDrawerProvider>
                  <MondooThemeProvider loggedIn={loggedIn}>
                    <TriageToolbarProvider>
                      <DialogsProvider>
                        <AuthenticatedAppRoutes />
                        <FeatureFlagsModal />
                      </DialogsProvider>
                    </TriageToolbarProvider>
                  </MondooThemeProvider>
                </AppNavDrawerProvider>
              </ScopeProvider>
            </ColorModeProvider>
          </ViewerProvider>
        </BrowserRouter>
      </GraphqlProvider>
    </RegionsProvider>
  ) : (
    <BrowserRouter>
      <UnauthenticatedApp />
    </BrowserRouter>
  );
};

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  // TODO: Remove FeatureFlagsProvider once Foundation themes flag is removed
  root.render(
    <AuthProvider>
      <FeatureFlagsProvider>
        <MondooThemeProvider>
          <App />
        </MondooThemeProvider>
      </FeatureFlagsProvider>
    </AuthProvider>,
  );
} else {
  throw new Error("Root container element is not defined");
}

// Check if the URL provided is relative
const isRelativeUrl = (urlToTest: string): boolean => {
  const outcome =
    new URL(document.baseURI).origin ===
    new URL(urlToTest, document.baseURI).origin;

  if (outcome === false) {
    throw new Error();
  }

  return true;
};
